// src/pages/ModificarAlumnos.jsx
import React from "react";
import "../styles/styles.css";
import "../styles/formularios.css";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import logoInstituto from "../components/images/logo.png";
import logoSEP from "../components/images/SEP_Logo_2019.png";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveApiBaseList } from "../utils/apiBase";

const describirSexo = (valor = "") => {
  const normalizado = valor.toString().trim().toUpperCase();
  if (["H", "M", "MASCULINO"].includes(normalizado)) {
    return "Masculino";
  }
  if (["F", "FEMENINO"].includes(normalizado)) {
    return "Femenino";
  }
  return normalizado || "Sin especificar";
};

/* ======================= HOOK DE LÓGICA ======================= */
function useModificarAlumnoLogica() {
  const [alumno, setAlumno] = useState(null);

  // Editables
  const [correo, setCorreo] = useState("");
  const [tipoSangre, setTipoSangre] = useState("");
  const [telefono, setTelefono] = useState("");

  // Estado de carga del alumno
  const [loadingAlumno, setLoadingAlumno] = useState(false);

  // --- Buscador unificado (Nombre o CURP) ---
  const [query, setQuery] = useState("");
  const [sugerencias, setSugerencias] = useState([]); // [{IdAlumno, NombreCompleto, CURP}]
  const [loadingSug, setLoadingSug] = useState(false);
  const debounceRef = useRef(null);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const url = `${base}${path}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          const error = new Error(text || `Error ${response.status}`);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get("content-type") || "";
        const trimmed = text.trim();
        if (contentType.includes("application/json") || trimmed.startsWith("{") || trimmed.startsWith("[")) {
          return trimmed ? JSON.parse(trimmed) : {};
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || "desconocido"})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error("No se pudo contactar al servidor.");
  }, [apiBases]);

  const postWithFallback = useCallback(async (path, payload) => {
    let lastError = null;
    for (const base of apiBases) {
      const url = `${base}${path}`;
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === "object") {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // Ignorar parseo fallido y usar texto bruto
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló el POST a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (trimmed && trimmed.startsWith("<")) {
          const error = new Error(`Respuesta inválida desde ${url}`);
          lastError = error;
          console.warn(`⚠️ Contenido no válido desde ${url}`);
          continue;
        }

        try {
          if (trimmed && (trimmed.startsWith("{") || trimmed.startsWith("["))) {
            const parsed = JSON.parse(trimmed);
            const message = parsed?.message || parsed?.msg || parsed?.resultado || trimmed;
            return { text: message, url, raw: parsed };
          }
        } catch (parseErr) {
          console.warn(`⚠️ No se pudo parsear respuesta JSON desde ${url}:`, parseErr);
        }

        return { text: trimmed, url };
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al hacer POST ${url}:`, error);
      }
    }

    throw lastError || new Error("No se pudo contactar al servidor.");
  }, [apiBases]);

  // Autocompletado con debounce usando /grupos/buscar-alumnos
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSugerencias([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoadingSug(true);
      try {
        const params = new URLSearchParams({ q: query.trim(), limit: "15" });
        const data = await fetchJsonWithFallback(`/grupos/buscar-alumnos?${params.toString()}`);
        setSugerencias(Array.isArray(data) ? data : []);
      } catch (error) {
        console.warn("⚠️ No se pudieron obtener sugerencias de alumnos:", error);
        setSugerencias([]);
      } finally {
        setLoadingSug(false);
      }
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [fetchJsonWithFallback, query]);

  // Al seleccionar sugerencia, cargamos detalle por CURP
  const cargarAlumnoPorCURP = useCallback(async (curp) => {
    if (!curp) return;
    try {
      setLoadingAlumno(true);
      const data = await fetchJsonWithFallback(
        `/alumnos/por-curp/${encodeURIComponent(curp)}`
      );
      setAlumno(data || null);
    } catch (e) {
      console.error("Error al obtener alumno por CURP:", e);
      Swal.fire("Error", "No se pudo obtener el alumno", "error");
    } finally {
      setLoadingAlumno(false);
    }
  }, [fetchJsonWithFallback]);

  const seleccionarSugerencia = useCallback(
    (s) => {
      setQuery("");
      setSugerencias([]);
      cargarAlumnoPorCURP(s.CURP);
    },
    [cargarAlumnoPorCURP]
  );

  const onKeyDownQuery = useCallback(
    (e) => {
      if (e.key === "Enter" && sugerencias.length > 0) {
        e.preventDefault();
        seleccionarSugerencia(sugerencias[0]);
      }
    },
    [sugerencias, seleccionarSugerencia]
  );

  // Sincroniza editables al cambiar el alumno
  useEffect(() => {
    if (!alumno) {
      setCorreo("");
      setTipoSangre("");
      setTelefono("");
      return;
    }
    setCorreo(alumno.Correo || "");
    setTipoSangre(alumno.TipoSangre || "");
    setTelefono((alumno.Telefono || "").toString().replace(/\D/g, "")); 
  }, [alumno]);

  const handleTelefonoChange = useCallback((value) => {
    const soloDigitos = value.replace(/\D/g, "").slice(0, 10);
    setTelefono(soloDigitos);
  }, []);

  // Modificar alumno (igual que tenías)
  const modificarAlumno = useCallback(async () => {
    if (!alumno) return;

    const telefonoSanitizado = (telefono || "").replace(/\D/g, "");
    if (telefonoSanitizado.length !== 10) {
      await Swal.fire("Atención", "El teléfono debe contener exactamente 10 dígitos.", "warning");
      return;
    }

    const payload = {
      nombre: alumno.Nombre || "",
      apellidoPaterno: alumno.ApellidoPaterno || "",
      apellidoMaterno: alumno.ApellidoMaterno || "",
      correo,
      tipoSangre,
      telefono: telefonoSanitizado,
    };

    try {
      const { text } = await postWithFallback(`/alumnos/modificar`, payload);

      await Swal.fire({
        icon: "success",
        title: "Modificado",
        text: text || "Alumno modificado correctamente",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (e) {
      console.error("❌ Error en modificación:", e);
      Swal.fire("Error", e.message || "No se pudo modificar el alumno", "error");
    }
  }, [alumno, correo, tipoSangre, telefono, postWithFallback]);

  const mostrarFormulario = useMemo(() => !!alumno, [alumno]);

  return {
    // Buscador
    query,
    setQuery,
    sugerencias,
    loadingSug,
    onKeyDownQuery,
    seleccionarSugerencia,
    // Alumno y carga
    alumno,
    loadingAlumno,
    mostrarFormulario,
    // Editables
    correo,
    setCorreo,
    tipoSangre,
    setTipoSangre,
    telefono,
    onTelefonoChange: handleTelefonoChange,
    // Acción
    modificarAlumno,
  };
}

/* ======================= PÁGINA ======================= */
const ModificarAlumnos = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSalir = () => {
    const rol = resolveRolBase(location.pathname);
    if (rol) {
      navigate(`/${rol}/inicio`, { replace: true });
    } else {
      navigate("/inicio", { replace: true });
    }
  };

  const {
    // buscador
    query,
    setQuery,
    sugerencias,
    loadingSug,
    onKeyDownQuery,
    seleccionarSugerencia,
    // alumno
    alumno,
    loadingAlumno,
    mostrarFormulario,
    // editables
  correo,
  setCorreo,
  tipoSangre,
  setTipoSangre,
  telefono,
  onTelefonoChange,
    // acción
    modificarAlumno,
  } = useModificarAlumnoLogica();

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Modificar Alumnos</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Buscador unificado: Nombre o CURP */}
      <div className="mb-4">
        <label htmlFor="buscadorAlumno" className="form-label">
          Buscar alumno
        </label>
        <div className="position-relative">
          <input
            id="buscadorAlumno"
            className="form-control"
            placeholder="Buscar alumno por nombre o CURP"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDownQuery}
            autoComplete="off"
          />

          {query && (loadingSug || sugerencias.length > 0) && (
            <div
              className="list-group shadow position-absolute w-100"
              style={{ zIndex: 10, maxHeight: 260, overflowY: "auto" }}
            >
              {loadingSug && (
                <div className="list-group-item text-muted">Buscando…</div>
              )}
              {!loadingSug && sugerencias.length === 0 && (
                <div className="list-group-item text-muted">Sin resultados</div>
              )}
              {!loadingSug &&
                sugerencias.map((s) => (
                  <button
                    key={s.IdAlumno}
                    type="button"
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    onClick={() => seleccionarSugerencia(s)}
                  >
                    <span>{s.NombreCompleto}</span>
                    <span className="badge bg-secondary">{s.CURP}</span>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Cargando alumno */}
      {loadingAlumno && (
        <div className="alert alert-info">Cargando alumno…</div>
      )}

      {/* ========= PARTE FINAL (NO MODIFICADA) ========= */}
      {/* Formulario */}
      {mostrarFormulario && alumno && (
        <div className="bg-light p-4 rounded shadow-sm">
          <h6 className="mb-3 fw-semibold">Datos del Alumno</h6>
          <form className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label">ID Alumno</label>
              <input
                type="text"
                className="form-control"
                value={alumno.IdAlumno || ""}
                disabled
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Nombre</label>
              <input
                type="text"
                className="form-control"
                value={alumno.Nombre || ""}
                disabled
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Apellido Paterno</label>
              <input
                type="text"
                className="form-control"
                value={alumno.ApellidoPaterno || ""}
                disabled
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Apellido Materno</label>
              <input
                type="text"
                className="form-control"
                value={alumno.ApellidoMaterno || ""}
                disabled
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">CURP</label>
              <input
                type="text"
                className="form-control"
                value={alumno.CURP || ""}
                disabled
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Sexo</label>
              <input
                type="text"
                className="form-control"
                value={describirSexo(alumno.Sexo)}
                disabled
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Correo</label>
              <input
                type="email"
                className="form-control"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Tipo de Sangre</label>
              <select
                className="form-select"
                value={tipoSangre}
                onChange={(e) => setTipoSangre(e.target.value)}
              >
                <option value="" disabled>
                  Seleccionar
                </option>
                <option>A+</option>
                <option>A-</option>
                <option>B+</option>
                <option>B-</option>
                <option>AB+</option>
                <option>AB-</option>
                <option>O+</option>
                <option>O-</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Teléfono</label>
              <input
                type="text"
                className="form-control"
                value={telefono}
                inputMode="numeric"
                pattern="^[0-9]{10}$"
                maxLength={10}
                onChange={(e) => onTelefonoChange(e.target.value)} 
              />
            </div>
            <div className="col-md-12 text-end mt-3">
              <button
                type="button"
                className="btn btn-warning px-4"
                onClick={modificarAlumno}
              >
                Modificar
              </button>
              <button
                type="button"
                className="btn btn-danger px-4 ms-2"
                onClick={handleSalir}
              >
                Salir
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ["directora", "coordinadora", "docente", "alumno"];
  const seg = pathname.split("/").filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem("role"));
  const map = { 1: "directora", 2: "coordinadora", 3: "docente", 4: "alumno" };
  return map[roleId] || null;
}

export default ModificarAlumnos;
